import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as fmt from '../format';
import { segmentLitres, snapVolume } from './arrivals';

const FILL = {
  tank: '#95de64',
  brew: '#95de64',
  rec: '#ffa940',
};

const LINK = {
  tank: '#52c41a',
  brew: '#52c41a',
  rec: '#fa8c16',
};

/** Horizontal-tangent curve from the brew bar into the ready bar. */
const connectorPath = (brew, ready, barW) => {
  const x1 = brew.x + barW + 1.5;
  const x2 = ready.x - 1.5;
  const y1 = (brew.yTop + brew.yBot) / 2;
  const y2 = (ready.yTop + ready.yBot) / 2;
  const span = Math.max(x2 - x1, 8);
  const bow = Math.min(span * 0.42, 48);
  return `M ${x1} ${y1} C ${x1 + bow} ${y1}, ${x2 - bow} ${y2}, ${x2} ${y2}`;
};

const KIND_RANK = { tank: 0, brew: 1, rec: 2 };

const compactLitres = (value) => {
  if (!value) return '';
  if (value >= 1000) return `${Math.round(value / 1000)}k`;
  return fmt.number(value);
};

const axisScale = (map) => {
  if (!map) return null;
  const axis = Object.values(map)[0];
  return axis && typeof axis.scale === 'function' ? axis.scale : null;
};

const clientToSvg = (svg, clientX, clientY) => {
  const point = svg.createSVGPoint();
  point.x = clientX;
  point.y = clientY;
  const matrix = svg.getScreenCTM();
  if (!matrix) return null;
  return point.matrixTransform(matrix.inverse());
};

const keepFraction = (lossPct) => 1 - Math.min(Math.max(Number(lossPct) || 0, 0), 90) / 100;

const TankBars = ({
  arrivals,
  weekStarts,
  canEdit,
  lossPct,
  onDraftChange,
  onRemove,
  onDragActive,
  xAxisMap,
  yAxisMap,
  offset,
}) => {
  const xScale = axisScale(xAxisMap);
  const yScale = axisScale(yAxisMap);
  const geomRef = useRef({});
  const dragRef = useRef(null);
  const [draggingId, setDraggingId] = useState(null);

  geomRef.current = {
    xScale,
    yScale,
    onDraftChange,
    onDragActive,
  };

  useEffect(() => () => {
    const drag = dragRef.current;
    if (!drag) return;
    drag.cleanup();
    dragRef.current = null;
    geomRef.current.onDragActive(false);
  }, []);

  if (!xScale || !yScale || !offset || !weekStarts.length) return <g />;

  const beginDrag = (event, segment, mode) => {
    event.preventDefault();
    event.stopPropagation();
    const svg = event.currentTarget.ownerSVGElement;
    const point = svg && clientToSvg(svg, event.clientX, event.clientY);
    const scaleY = geomRef.current.yScale;
    if (!svg || !point || !scaleY || !scaleY.invert) return;

    const { item } = segment;
    svg.setPointerCapture(event.pointerId);
    setDraggingId(item.id);
    onDragActive(true);

    const drag = {
      id: item.id,
      mode,
      weekIdx: item.weekIdx,
      leadWeeks: segment.role === 'brew' ? segment.leadWeeks : 0,
      measuresTank: segment.role === 'brew',
      volumeLitres: item.volumeLitres,
      startPackaged: item.packagedLitres,
      startYLitres: scaleY.invert(point.y),
    };

    const move = (e) => {
      const { current } = dragRef;
      const { current: geom } = geomRef;
      if (!current || !geom.yScale || !geom.xScale) return;
      const next = clientToSvg(svg, e.clientX, e.clientY);
      if (!next) return;
      if (current.mode === 'resize') {
        const yLitres = geom.yScale.invert(next.y);
        const delta = yLitres - current.startYLitres;
        const volumeLitres = current.measuresTank
          ? snapVolume(current.volumeLitres + delta)
          : snapVolume(Math.max(0, current.startPackaged + delta) / keepFraction(lossPct));
        geom.onDraftChange(current.id, { weekIdx: current.weekIdx, volumeLitres });
        return;
      }
      const brewWeek = Math.round(geom.xScale.invert(next.x));
      const weekIdx = brewWeek + current.leadWeeks;
      geom.onDraftChange(current.id, { weekIdx, volumeLitres: current.volumeLitres });
    };

    const end = (e) => {
      drag.cleanup();
      try {
        svg.releasePointerCapture(e.pointerId);
      } catch (err) {
        // Capture was already released.
      }
      if (dragRef.current === drag) dragRef.current = null;
      setDraggingId(null);
      geomRef.current.onDragActive(false);
    };

    drag.cleanup = () => {
      svg.removeEventListener('pointermove', move);
      svg.removeEventListener('pointerup', end);
      svg.removeEventListener('pointercancel', end);
    };
    dragRef.current = drag;
    svg.addEventListener('pointermove', move);
    svg.addEventListener('pointerup', end);
    svg.addEventListener('pointercancel', end);
  };

  const horizon = weekStarts.length;
  const segments = [];
  arrivals.forEach((item) => {
    if (!(item.packagedLitres > 0)) return;
    if (item.weekIdx < 0 || item.weekIdx >= horizon) return;
    const brewWeek = item.brewWeekIdx;
    const split = brewWeek != null && brewWeek >= 0 && brewWeek < horizon && brewWeek !== item.weekIdx;
    if (split) {
      segments.push({
        item,
        weekIdx: brewWeek,
        role: 'brew',
        leadWeeks: item.weekIdx - brewWeek,
      });
      segments.push({
        item,
        weekIdx: item.weekIdx,
        role: 'ready',
        leadWeeks: item.weekIdx - brewWeek,
      });
    } else {
      segments.push({
        item,
        weekIdx: item.weekIdx,
        role: 'single',
        leadWeeks: 0,
      });
    }
  });

  const byWeek = new Map();
  segments.forEach((segment) => {
    if (!byWeek.has(segment.weekIdx)) byWeek.set(segment.weekIdx, []);
    byWeek.get(segment.weekIdx).push(segment);
  });

  const step = Math.abs(xScale(1) - xScale(0)) || 18;
  const barW = Math.min(18, step * 0.8);
  const shapes = [];
  const labels = [];
  const links = [];
  const placed = new Map();
  let dragLabel = null;

  byWeek.forEach((items, weekIdx) => {
    const ordered = items.slice().sort((a, b) => (
      (KIND_RANK[a.item.kind] || 0) - (KIND_RANK[b.item.kind] || 0)
      || a.item.id.localeCompare(b.item.id)
      || a.role.localeCompare(b.role)
    ));
    let bottom = 0;
    const cx = xScale(weekIdx);
    let draggingHere = false;
    ordered.forEach((segment) => {
      const { item } = segment;
      const litres = segmentLitres(item, segment.role);
      const topLitres = bottom + litres;
      const yTop = yScale(topLitres);
      const yBot = yScale(bottom);
      const height = Math.max(yBot - yTop, 0);
      const x = cx - barW / 2;
      const interactive = canEdit && item.editable && segment.role !== 'ready';
      const key = `${item.id}:${segment.role}`;
      placed.set(key, {
        x, yTop, yBot, height,
      });
      if (segment.role === 'ready') {
        links.push({
          id: item.id,
          kind: item.kind,
          readyKey: key,
          brewKey: `${item.id}:brew`,
        });
      }
      if (item.id === draggingId && interactive) {
        draggingHere = true;
        dragLabel = {
          x: cx,
          y: yTop - 8,
          item,
          weekIdx: segment.weekIdx,
        };
      }
      shapes.push(
        <rect
          key={key}
          x={x}
          y={yTop}
          width={barW}
          height={height}
          fill={FILL[item.kind] || FILL.brew}
          fillOpacity={segment.role === 'ready' ? 0.4 : 1}
          stroke="rgba(255,255,255,0.9)"
          strokeWidth={1}
          style={{ cursor: interactive ? 'ew-resize' : 'default', touchAction: 'none' }}
          pointerEvents={segment.role === 'ready' ? 'none' : undefined}
          onPointerDown={interactive ? (e) => beginDrag(e, segment, 'move') : undefined}
        />,
      );
      if (item.edited || item.id === draggingId) {
        shapes.push(
          <rect
            key={`${key}-outline`}
            x={x}
            y={yTop}
            width={barW}
            height={height}
            fill="none"
            stroke="#262626"
            strokeDasharray="3 2"
            pointerEvents="none"
          />,
        );
      }
      if (interactive) {
        const handleH = height > 10 ? 6 : Math.max(height / 2, 2);
        labels.push(
          <rect
            key={`${key}-handle`}
            x={x}
            y={yTop}
            width={barW}
            height={handleH}
            fill="#262626"
            fillOpacity={0.45}
            style={{ cursor: 'ns-resize', touchAction: 'none' }}
            onPointerDown={(e) => beginDrag(e, segment, 'resize')}
          />,
        );
        if (item.id !== draggingId) {
          labels.push(
            <g
              key={`${key}-remove`}
              style={{ cursor: 'pointer' }}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onRemove(item.id);
              }}
            >
              <circle cx={x + barW + 7} cy={yTop + 4} r={6} fill="#fff" stroke="#cf1322" />
              <text
                x={x + barW + 7}
                y={yTop + 8}
                textAnchor="middle"
                fontSize={11}
                fill="#cf1322"
                pointerEvents="none"
              >
                ×
              </text>
            </g>,
          );
        }
      }
      bottom = topLitres;
    });
    if (!draggingHere && bottom > 0) {
      labels.push(
        <text
          key={ordered.map((segment) => `${segment.item.id}:${segment.role}`).join('-')}
          x={cx}
          y={yScale(bottom) - 5}
          textAnchor="middle"
          fontSize={11}
          fontWeight={700}
          fill="#262626"
          pointerEvents="none"
        >
          {compactLitres(bottom)}
        </text>,
      );
    }
  });

  const labelWeek = dragLabel ? weekStarts[dragLabel.weekIdx] : null;
  const labelText = dragLabel && labelWeek
    ? `${fmt.litres(dragLabel.item.volumeLitres)} · wk of ${fmt.shortDate(labelWeek)}`
    : '';

  return (
    <g>
      <defs>
        <clipPath id="forecasting-tank-bars-clip">
          <rect
            x={offset.left}
            y={offset.top}
            width={offset.width}
            height={offset.height}
          />
        </clipPath>
      </defs>
      <g clipPath="url(#forecasting-tank-bars-clip)">
        {links.map((link) => {
          const brew = placed.get(link.brewKey);
          const ready = placed.get(link.readyKey);
          if (!brew || !ready) return null;
          const d = connectorPath(brew, ready, barW);
          return (
            <g key={`${link.id}-link`} pointerEvents="none">
              <path d={d} fill="none" stroke="#fff" strokeWidth={3.5} strokeLinecap="round" />
              <path
                d={d}
                fill="none"
                stroke={LINK[link.kind] || LINK.brew}
                strokeWidth={1.5}
                strokeLinecap="round"
              />
            </g>
          );
        })}
        {shapes}
      </g>
      {labels}
      {dragLabel && (
        <text
          x={dragLabel.x}
          y={dragLabel.y}
          textAnchor="middle"
          fontSize={11}
          fontWeight={700}
          fill="#262626"
          stroke="#fff"
          strokeWidth={3}
          paintOrder="stroke"
          pointerEvents="none"
        >
          {labelText}
        </text>
      )}
    </g>
  );
};

TankBars.propTypes = {
  arrivals: PropTypes.arrayOf(PropTypes.object),
  weekStarts: PropTypes.arrayOf(PropTypes.string),
  canEdit: PropTypes.bool,
  lossPct: PropTypes.number,
  onDraftChange: PropTypes.func,
  onRemove: PropTypes.func,
  onDragActive: PropTypes.func,
  xAxisMap: PropTypes.shape({}),
  yAxisMap: PropTypes.shape({}),
  offset: PropTypes.shape({
    left: PropTypes.number,
    top: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
  }),
};

TankBars.defaultProps = {
  arrivals: [],
  weekStarts: [],
  canEdit: false,
  lossPct: 0,
  onDraftChange: () => {},
  onRemove: () => {},
  onDragActive: () => {},
  xAxisMap: null,
  yAxisMap: null,
  offset: null,
};

export default TankBars;
